import { AGENT_RUN_CATEGORIES } from '@erp/rpc';
import {
  hidesOnlySkipped,
  type RunHistoryFilter,
} from './agentRunHistoryFilter';

/** Why the History list is empty, which picks the message it shows. */
export type HistoryEmptyReason =
  | 'never-ran'
  | 'none-in-range'
  | 'only-skipped'
  | 'only-skipped-in-range'
  | 'filtered'
  | 'nothing-selected';

function hasRange(filter: RunHistoryFilter): boolean {
  return filter.range.from !== undefined || filter.range.to !== undefined;
}

/**
 * An older backend sends no hidden count: say only what the filter implies,
 * never that the agent has not run.
 */
function reasonWithoutCount(filter: RunHistoryFilter): HistoryEmptyReason {
  if (hasRange(filter)) return 'none-in-range';
  return filter.excludeCategories.length > 0 ? 'filtered' : 'never-ran';
}

function reasonWithCount(
  filter: RunHistoryFilter,
  hiddenCount: number,
): HistoryEmptyReason {
  if (hiddenCount === 0) {
    return hasRange(filter) ? 'none-in-range' : 'never-ran';
  }
  if (!hidesOnlySkipped(filter)) return 'filtered';
  return hasRange(filter) ? 'only-skipped-in-range' : 'only-skipped';
}

/**
 * Why an empty History list is empty. `hiddenCount` is how many runs the
 * run-type filter left out, or null when the server did not say.
 */
export function historyEmptyReason(
  filter: RunHistoryFilter,
  hiddenCount: number | null,
): HistoryEmptyReason {
  if (filter.excludeCategories.length === AGENT_RUN_CATEGORIES.length) {
    return 'nothing-selected';
  }
  return hiddenCount === null
    ? reasonWithoutCount(filter)
    : reasonWithCount(filter, hiddenCount);
}
